import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { styled } from "@mui/material/styles";
import { grey } from "designSystem/styles/color";
import { scrollToElement } from "designSystem/utilities/scroll-to-element";
import { CSS_ANCHOR_RESET, itemListStyle, navListStyle } from "designSystem/styles/style-functions";
import OverflowCombinedText from "designSystem/components/text/OverflowCombinedText";
import { SPACE } from "designSystem/components/text/Text";
import Tooltip, { TooltipProps } from "designSystem/components/tooltip/Tooltip";
import { DataSetupItem, DataSetupSection, SUBTYPE_TYPES, useDataSetupStore } from "csi/state/datasetup";
import { default as React, forwardRef, FunctionComponent, useCallback, useState } from "react";

interface DataSetupSingleItemProps extends Omit<DataSetupItemListProps, "items" | "selectedItem"> {
	/** the item to be rendered */
	item: DataSetupItem;

	/** indicated if it's selected */
	selected: boolean;
}

const PREFIX = "DataSetupSingleItemList";

const classes = {
	group: `${PREFIX}Group`,
	normal: `${PREFIX}Normal`,
	search: `${PREFIX}Search`,
};

const StyledList = styled(List)((
	{
		theme,
	},
) => ({
	[`& .${classes.group}`]: {
		color: grey[900],
	},
	[`& .${classes.normal}`]: {
		...navListStyle(theme),
		paddingLeft: 24,
	},
	[`& .${classes.search}`]: itemListStyle(theme),
}));

const DataSetupSingleItem = forwardRef<HTMLAnchorElement, DataSetupSingleItemProps>((props, ref) => {
	const { forSearch, item, selected, selectedSection } = props;
	const dataSetupStore = useDataSetupStore();
	const tooltipProps: Partial<TooltipProps> = { placement: "right" };
	const searchText = dataSetupStore.searchText.toLocaleLowerCase();

	const [overflows, setOverflows] = useState(false);

	const group = selectedSection?.findGroupWithItem(item.id);
	const isPartOfItemName = item.name.toLocaleLowerCase()
		.includes(searchText);
	const showGroupName = SUBTYPE_TYPES[item.nodeType] || !isPartOfItemName;

	const groupName = forSearch && showGroupName ? (group?.name) : "";

	const separator = forSearch ? `${SPACE}-${SPACE}` : "";
	const title = item.name + separator + groupName;

	const contentRef = useCallback((node: HTMLElement) => {
		if (node) {
			setOverflows(node.scrollWidth > node.clientWidth);
		}
	}, [groupName]);

	return (
		<Tooltip
			disableHoverListener={!overflows}
			key={item.id}
			title={title}
			{...tooltipProps}
		>
			<ListItem
				className={`${CSS_ANCHOR_RESET} ${forSearch ? classes.search : classes.normal}`}
				component="a"
				href={item.href}
				ref={ref}
				selected={selected}
			>
				<OverflowCombinedText
					mainText={item.name}
					ref={contentRef}
					secondaryText={groupName}
					secondaryTextProps={{ className: classes.group }}
					separator={separator}
					separatorProps={{ className: classes.group }}
				/>
			</ListItem>
		</Tooltip>
	);
});

export interface DataSetupItemListProps {
	/** indicates if displaying items as search results */
	forSearch?: boolean;

	/** list of items to display */
	items: DataSetupItem[];

	/** selected item in list */
	selectedItem?: DataSetupItem;

	/** selected section in list */
	selectedSection?: DataSetupSection;
};

const DataSetupItemList: FunctionComponent<DataSetupItemListProps> = (props) => {
	const { forSearch, items, selectedItem, selectedSection } = props;

	const selectedRef = useCallback((domNode) => {
		if (!forSearch) {
			scrollToElement(domNode);
		}
	}, [forSearch, selectedItem]);

	return (
		<StyledList>
			{
				items.map((item) => (
					<DataSetupSingleItem
						forSearch={forSearch}
						item={item}
						key={item.id}
						ref={item === selectedItem ? selectedRef : null}
						selected={item == selectedItem}
						selectedSection={selectedSection}
					/>
				),
				)
			}
		</StyledList>
	);
};

export default DataSetupItemList;